// ─────────────────────────────────────────────────────────────
// TERROR — STORY MODE DATA
// The Minerium of Sir H'osh Vrown
//
// ⬡ THE SCALE RULING (caP'n, 2026-08-18, and it settled the whole
// naming question): *"Gotta just rename it to Minerium. Emperiums are
// reserved for infinitely bigger spaces — Emperiums are like full-on
// universes/dimensions. This is finite."*
//
// So the two words are NOT big-and-small versions of one thing. They
// are different KINDS of thing:
//
//   EMPERIUM — a universe. A dimension. Infinite, and a Lord's. The
//              four metas are Emperiums (Space, Phantom, Time,
//              Dragon), the Unstable Emperium is one, and V'ael's is
//              one. None of them are places you walk across.
//   MINERIUM — FINITE. A traversable overworld with edges. Built by a
//              being under Lord rank, gifted by a Lord, or "other ways
//              hehe" — that third door is open on purpose.
//
// Everything in Terror's Story Mode is the second. The rename swept 76
// occurrences on 2026-08-18; every surviving "Emperium" in this
// codebase is either a code symbol, a persisted Firebase key, or one
// of the universe-scale things listed above.
//
// ✅ RESOLVED — WHY A BEB HOLDS A KINGDOM (caP'n): *"Yes, it was given
// to SHV for being an amazing colonel."* The Great Omnis granted it.
// The abandoned place is what was given: a Beb does not get handed a
// kingdom out of thin air — he gets handed one somebody walked away
// from. That keeps every line of the Marsh's existing lore true and
// explains why he behaves like a man waiting to be asked to leave.
//
// ⚠ ITS FORMER LORD IS DELIBERATELY UNNAMED (caP'n, 2026-08-18): X'aol is to
// be MYSTERIOUS — his only mention in the whole game is the one line in
// the Rulebook, and Story Mode names him nowhere. A Minerium whose
// owner is never named is more mysterious than one that names him in
// eight places, so this is not a hole; it is the point.
// ─────────────────────────────────────────────────────────────

import { getAllGrimoireCards } from "./cards";
// H30 (multiplayer expansion) — sourcing the AI seat's REAL deck for
// Story Co-op. Both are leaves (starterDecks.js imports nothing;
// gameManager.js only reaches firebase/replay), so neither imports
// this file back — a one-way street, no cycle.
import { getStarterDeck } from "./starterDecks";
import { drawAndDepleteCards } from "./gameManager";

// Dialogue is styled as ancient Terror script —
// formal, cryptic, weight behind every word.

export const STORY_INTRO = {
  title: "The Abandoned Minerium",
  lore: `In a time before the Unstable World was Au Gratin, a Lord ruled from a Minerium carved from the fabric of Fate itself. When J'ynxi's devious scheme backfired and was sealed away, that Lord — pained by the sealing — abandoned the Minerium and was never seen again. Nobody left here will say the name, and it is not clear any of them still can.

The Minerium still stands. Its gates still breathe. And its loyal soldiers still guard what was left behind.

You are an Aenarchist. You have heard the rumors — that deep within the Minerium lies a treasure that can reshape the Unstable World. Whether that treasure is power, truth, or something far older... you will not know until you reach it.

Brave the Minerium. Face what guards it. And when you reach the end —

J'ynxi will be waiting.`,
  worldDescription: `The Unstable World is untapped potential — gold and diamonds inside of caves, whirlpools that descend into black holes. Space and time are merely constructs here. Seeing is no longer believing.`,
};

// ─────────────────────────────────────────────────────────────
// DUNGEONS
// ─────────────────────────────────────────────────────────────

export const DUNGEONS = [
  {
    id: 1,
    name: "Outskirts of the Minerium",
    subtitle: "The Outer Veil",
    description: "The outermost ring of the abandoned Minerium. Three loyal soldiers stand watch over the gate. They have not moved since its Lord departed. They do not intend to.",
    flavor: "◆ The air here tastes of forgotten oaths. ◆",
    unlocked: true,
    opponents: ["suhnker", "zhefir", "tyrent"],
    rewardPack: "special",
    rewardCrests: 1,
    backgroundGradient: "linear-gradient(180deg, #0a0012, #1a0030, #0d001a)",
    accentColor: "#7c3aed",
  },
  // Dungeons 2–7 to be revealed as story expands
  {
    id: 2,
    name: "The Inner Halls",
    subtitle: "Coming Soon",
    description: "Beyond the outer gate lies the heart of the Minerium. What waits within is not yet known.",
    flavor: "◆ The gate is sealed. For now. ◆",
    unlocked: false,
    opponents: [],
    rewardPack: "special",
    rewardCrests: 1,
    backgroundGradient: "linear-gradient(180deg, #001020, #002040, #001520)",
    accentColor: "#1e40af",
  },
  {
    id: 3,
    name: "The Khao Vaults",
    subtitle: "Coming Soon",
    description: "Rumors speak of a vault where the Minerium's Lord stored captured Khao. The very walls hum with contained power.",
    flavor: "◆ Power seeks those who seek power. ◆",
    unlocked: false,
    opponents: [],
    rewardPack: "special",
    rewardCrests: 1,
    backgroundGradient: "linear-gradient(180deg, #100010, #200030, #150020)",
    accentColor: "#9333ea",
  },
  {
    id: 7,
    name: "The Throne of J'ynxi",
    subtitle: "Final Dungeon",
    description: "The Solrend that started everything. J'ynxi awaits — sealed, but never silent. You have come too far to turn back.",
    flavor: "◆ With every victory, J'ynxi twists. With every defeat, J'ynxi turns. ◆",
    unlocked: false,
    opponents: ["jynxi"],
    rewardPack: "special",
    rewardCrests: 1,
    backgroundGradient: "linear-gradient(180deg, #1a0000, #3a0010, #1a0005)",
    accentColor: "#dc2626",
    isFinal: true,
  },
];

// ─────────────────────────────────────────────────────────────
// OPPONENTS
// ─────────────────────────────────────────────────────────────

export const OPPONENTS = {

  suhnker: {
    id: "suhnker",
    name: "S'uhnker",
    title: "Sentinel of the Outer Gate",
    dungeonId: 1,
    difficulty: "champion",
    starterDeckId: "starter_h09",
    avatarIcon: "⚔",
    accentColor: "#3b82f6",
    lore: "A being of few words and absolute conviction. S'uhnker has stood at the outer gate of the Minerium since the Lord's departure — not out of command, but out of choice. To him, the gate is sacred. You are not.",
    dialogue: {
      approach: [
        "◆ This gate has not opened in an age. It will not open for you.",
        "◆ Turn back, Aenarchist. What lies within is not yours to find.",
      ],
      onWin: [
        "◆ You are... stronger than the gate anticipated.",
        "◆ Pass, then. But know — those beyond are far less forgiving than I.",
      ],
      onLoss: [
        "◆ The gate holds. As it always has.",
        "◆ Come back when you understand what a Solrend truly costs.",
      ],
      midBattle: [
        "◆ Your Escendants fight well. They will not be enough.",
        "◆ H-09 does not fall easily, Aenarchist.",
      ],
    },
    rewardCrests: 1,
  },

  zhefir: {
    id: "zhefir",
    name: "Z'hefir",
    title: "Keeper of Shadows",
    dungeonId: 1,
    difficulty: "champion",
    starterDeckId: "starter_shadow_fox",
    avatarIcon: "◇",
    accentColor: "#6366f1",
    lore: "Z'hefir does not guard the gate. Z'hefir guards what the gate guards — the shadows between the stones, the spaces where light cannot reach. She has watched a thousand Aenarchists approach. She has watched all of them leave.",
    dialogue: {
      approach: [
        "◆ Your shadow arrived before you did. It told me everything I need to know.",
        "◆ The Phantom beasts answer to me here. Tread carefully.",
      ],
      onWin: [
        "◆ Hmm. Your shadow fights harder than you do. Interesting.",
        "◆ You have earned passage. Whether you survive what follows — that is another matter.",
      ],
      onLoss: [
        "◆ Even your shadow retreats now. Do the same.",
        "◆ The dark was never yours to command.",
      ],
      midBattle: [
        "◆ Intangible. You cannot touch what you cannot see.",
        "◆ The Shadow Fox has no interest in mercy.",
      ],
    },
    rewardCrests: 1,
  },

  tyrent: {
    id: "tyrent",
    name: "T'yrent",
    title: "The Last Flame of the Minerium",
    dungeonId: 1,
    difficulty: "lord",
    starterDeckId: "starter_chaos_dragon",
    avatarIcon: "◈",
    accentColor: "#dc2626",
    lore: "The strongest of the three outer sentinels. T'yrent was not assigned to the Minerium — T'yrent chose it, because nothing else in the Unstable World posed a sufficient challenge. The Dragon line bows to no one. T'yrent bowed to the Lord who left, and to nothing since.",
    dialogue: {
      approach: [
        "◆ I was beginning to think no one worthy would ever arrive. You may disappoint me yet.",
        "◆ Two of my companions have already fallen. I am not surprised. I am the last line for a reason.",
      ],
      onWin: [
        "◆ ...You may enter. Do not make me regret this.",
        "◆ The treasure within — it was always meant for someone like you. The Lord knew this. I did not believe it until now.",
      ],
      onLoss: [
        "◆ Even fire has its hierarchy, Aenarchist. Today you stand beneath it.",
        "◆ Return when the Chaos Dragon no longer frightens you.",
      ],
      midBattle: [
        "◆ Beings with more than 10 Lyt are instantly ruined. Remember that.",
        "◆ The Chaos Dragon does not lose. It simply... delays victory for its adversary.",
      ],
    },
    rewardCrests: 1,
    isBoss: true,   // final opponent of dungeon 1 — triggers dungeon clear
  },

  // J'ynxi — final boss, locked until dungeon 7
  jynxi: {
    id: "jynxi",
    name: "J'ynxi",
    title: "The Nefarious",
    dungeonId: 7,
    difficulty: "lord",
    starterDeckId: "starter_chaos_dragon",  // special deck to be built
    avatarIcon: "◆",
    accentColor: "#d4a84b",
    lore: "In a time before the Unstable World was Au Gratin, J'ynxi the Nefarious wrought this devious scheme disguised as a duel — its goal, to steal Anima from its adversary. The malicious plot backfired and resulted in the eternal sealing of J'ynxi. A fate that adversary was pained to make reality, and one nobody here will name aloud. You stand now at the end of everything. J'ynxi has been waiting for an Aenarchist strong enough to release it. The question is — what happens after?",
    dialogue: {
      approach: [
        "◆ Oh. You actually made it. How delightful.",
        "◆ Every win you claimed. Every defeat you suffered. Every card you drew. I felt all of it.",
        "◆ You did not come here to free me. But that is what will happen anyway.",
      ],
      onWin: [
        "◆ ...Yes. This is the one. The Lord always knew.",
        "◆ You have done what no Aenarchist has done before. J'ynxi is released. And J'ynxi is... grateful.",
        "◆ The Unstable World was never stable. Now it is yours to shape.",
      ],
      onLoss: [
        "◆ Did you think this would be easy? That is precious.",
        "◆ Come back. I will be here. I have been here for quite some time.",
        "◆ With every defeat, J'ynxi turns. Do you feel it?",
      ],
      midBattle: [
        "◆ J'ynxi uses every card type. Did you prepare for that?",
        "◆ The Affinity belongs to me. It has always belonged to me.",
        "◆ With every victory, J'ynxi twists. With every defeat, J'ynxi turns.",
      ],
    },
    rewardCrests: 1,
    isFinalBoss: true,
  },
};

// ─────────────────────────────────────────────────────────────
// INNER EMPERIUM VILLAINS — the endless ladder
// Each villain gates on innerWins (total Inner Minerium victories)
//
// ⭐ WIRED 2026-08-20, AND `storyOnly` ARRIVES WITH THE WIRING.
// `innerWinsRequired` was authored on all eight of these and read by
// NOTHING; `innerWins` was rendered and written by nothing. Both ends
// are live now — progression.recordDepthResult counts a Totem win,
// InnerMineriumSelect gates the picker on it.
//
// But three of these eight are no longer available to be climbed to.
// H'osh Vrown's Lyt Avatar is Story Mode's DOOR (WORLD_GATES), his Lyf
// Avatar is its INTERCEPTION at eight Sol Shards (AMBUSHES), and the
// man himself is the two-phase FINAL on the Crimson Jewel. Offering
// all three again as rungs on a grindable ladder would let a player
// meet the game's ending in a side menu before the story reached it.
//
// So they carry `storyOnly: true` and the picker filters them out.
// NOTHING IS DELETED — every field, every authored line and every
// reward is exactly where it was, `resolveBoss` still finds them by id
// for the three places that legitimately field them, and removing one
// word puts a row back on the ladder.
// ─────────────────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════════
// ⬡ THE MINERIUM — new canon, 2026-08-18 (caP'n)
// ────────────────────────────────────────────────────────────────────
// ⚠ SUPERSEDED 2026-08-18, and the correction matters: this table's
// first draft called a Minerium "a tiny Emperium." It is not one.
// caP'n: *"Emperiums are reserved for infinitely bigger spaces —
// full-on universes/dimensions. This is finite."* An Emperium is not a
// big Minerium; they are different kinds of thing, and only one of
// them has edges you can walk to. See this file's header.
//
// A Minerium can belong to ANYONE, and the three ways to get one are
// deliberately not two:
//
//   1. BUILT by a being under Lord rank — ambition without the ladder.
//   2. GIFTED by a Lord — patronage, and therefore a leash.
//   3. "OTHER WAYS HEHE" — caP'n's own third door, left open on
//      purpose. `origin` below is a STRING, not an enum, precisely so
//      the third way costs a row and not a session. (THE EXCEPTION LAW —
//      the two named ways are the ones that exist today, not the set
//      that can exist.)
//
// Sir H'osh Vrown is the first: a BEB who holds a kingdom. That is the
// whole character in one fact — the lowest rank in the game, with a
// throne, granted by the Great Omnis. Everything he says is downstream
// of being a Beb who out-earned his own ladder.
//
// ⚠ Every line of his dialogue below other than the exchange caP'n
// wrote is V'ael's placeholder. His two authored lines are marked.
// ════════════════════════════════════════════════════════════════════

export const MINERIA = [
  {
    id: "hosh_vrown",
    name: "The Minerium of Sir H'osh Vrown",
    holder: "Sir H'osh Vrown",
    origin: "gifted",              // built | gifted | ...whatever the third is
    grantedBy: "The Great Omnis",
    // ⭐ WHY (caP'n, 2026-08-18): *"it was given to SHV for being an
    // amazing colonel."* Not luck, not inheritance — payment.
    grantedFor: "service as Colonel of Omnis",
    accent: "#7c3aed",
    // Reached through the blue portal, which has no destination — see
    // THE BLUE PORTAL in the cutscene table.
    blurb: "A kingdom the size of a held breath, granted to a Beb who earned it. He has never once been asked to leave, and behaves like someone waiting to be.",
  },
];

export const MINERIUM_BY_ID = Object.fromEntries(MINERIA.map(m => [m.id, m]));

export const INNER_VILLAINS = [
  // ══ SIR H'OSH VROWN — Colonel of Omnis, King of his Minerium ══
  // caP'n, 2026-08-18. THREE separate fights, then a two-phase final:
  //   · his LYT Avatar  — barred at the door, before you may even enter
  //   · his LYF Avatar  — later, inside
  //   · his REAL Avatar — the final, which does not end when it falls,
  //                       because behind it is the man himself.
  //
  // The Avatars are three encounters and the final is ONE node with two
  // phases — "1 duel, 1 node, 1 interaction." See bossPhases.js.
  {
    id: "hosh_lyt",
    storyOnly: true,   // ⭐ Story Mode fields this one — see the banner above
    name: "H'osh Vrown's Lyt Avatar",
    title: "The Door",
    innerWinsRequired: 0,
    difficulty: "Beb",
    starterDeckId: "starter_h01",
    avatarIcon: "\u25c7",
    accentColor: "#a78bfa",
    minerium: "hosh_vrown",
    lore: "Not him. The part of him that answers the door — all reach and no weight, and it has never needed to be more than that, because nobody who has knocked has been worth the rest.",
    dialogue: {
      // ⭐ AUTHORED BY caP'n, verbatim. Do not paraphrase.
      approach: [
        "Bold of you two to enter without V'ael's Avatar to assist you.",
        "Or her wretched groundskeeper. He has so many...",
        "That foul beast.",
      ],
      onWin: ["The door is a formality.", "It always was."],
      onLoss: ["Then you never see the King.", "Most don't."],
    },
    rewardCrests: 1,
  },
  {
    id: "hosh_lyf",
    storyOnly: true,   // ⭐ Story Mode fields this one — see the banner above
    name: "H'osh Vrown's Lyf Avatar",
    title: "The Keep",
    innerWinsRequired: 1,
    difficulty: "Champion",
    starterDeckId: "starter_dg0",
    avatarIcon: "\u25c8",
    accentColor: "#8b5cf6",
    minerium: "hosh_vrown",
    lore: "The half of him that stays standing. Where the Lyt Avatar was all reach, this one is all refusal — it does not try to end you, it simply declines to end.",
    dialogue: {
      approach: [
        "You got past the door. Good.",
        "The door was never the wall.",
      ],
      onWin: ["...he will want to see you now.", "I would not have chosen that for you."],
      onLoss: ["You may sit down.", "Everyone does, eventually."],
    },
    rewardCrests: 1,
  },
  {
    id: "hosh_vrown",
    storyOnly: true,   // ⭐ Story Mode fields this one — see the banner above
    name: "H'osh Vrown's Avatar",
    title: "Colonel of Omnis",
    innerWinsRequired: 2,
    difficulty: "Lord",
    // ⭐ ANT, BY DECREE. caP'n, 2026-08-21: *"just give final boss Ant
    // deck"* — and the lore agrees, because *"SHV is an Ant Beb that
    // was recruited by Marshal Malone after a crushing defeat on an
    // attempt on one of Omnis' strongholds."* He was a Premier Dragon
    // deck for months, which was a good fight and the wrong man.
    //
    // ⚠ This field was DECORATION until the same day — every launcher
    // rolled a random starter deck and ignored it. See the deck block
    // in App.handleEnterNPC.
    starterDeckId: "starter_antdroid",
    // ⭐ THE SPECIES LAW (caP'n, 2026-08-22): *"every single being in the
    // minerium has to be a species of the canon current Terror, do not
    // create random species, they must be from the 12 species I have
    // provided."* Sir H'osh Vrown is an ANTDROID — an Ant Beb Marshal
    // Malone recruited after a crushing defeat on an attempt at one of
    // Omnis' strongholds. The Antdroid line he commands is the only
    // card set in the Minerium that drops from his own body.
    species: "Antdroid",
    avatarIcon: "\u2726",
    accentColor: "#7c3aed",
    minerium: "hosh_vrown",
    lore: "A Beb with a kingdom. The Great Omnis granted it and has never once come to look at it, and Sir H'osh Vrown has spent every day since being worth the granting in case they do.",
    // ⭐ THE SECOND WIND. One entry = two phases. The board persists,
    // his Anima and Khao refresh, the Avatar drops, and the man is
    // standing where it was. See bossPhases.js.
    phases: [
      {
        name: "Sir H'osh Vrown",
        title: "King of this Minerium",
        say: "The Avatar was the courtesy.",
        anima: "refresh",
        khao: "refresh",
        accentColor: "#c084fc",
      },
    ],
    dialogue: {
      // ⭐ AUTHORED BY caP'n, verbatim — the exchange at the gate.
      approach: [
        "Bold of you two to enter without V'ael's Avatar to assist you, or her wretched groundskeeper — he has so many... that foul beast.",
        "[We declined the offer. Do you think us so weak as not to be able to rescue our children, no matter where they are?]",
        "*smirks* You're either naive or...",
      ],
      // ⭐ AUTHORED. "Them" is THE TREASURES, ruled by caP'n
      // 2026-08-18 — not the children. Story One concludes to no
      // avail and the Champion says so a minute later; this line is
      // a king handing over a vault, not a father handing back sons.
      onWin: [
        "...",
        "Then take them. They were never mine to keep.",
        "Tell V'ael I was courteous.",
      ],
      onLoss: [
        "Naive, then.",
        "I did wonder.",
      ],
    },
    rewardCrests: 1,
  },

  {
    id: "vael",
    name: "V'ael",
    title: "The One Who Was Left",
    innerWinsRequired: 0,
    difficulty: "Champion",
    starterDeckId: "starter_shadow_fox",
    avatarIcon: "◇",
    accentColor: "#c9a84c",
    lore: "V'ael did not choose to guard the Inner Minerium. V'ael simply never left. Whatever reason brought them here has been forgotten — even by V'ael. What remains is presence, patience, and an absolute intolerance for those who enter without understanding what they're walking into.",
    dialogue: {
      approach: [
        "You opened the door.",
        "I didn't invite you.",
        "Those are different things.",
      ],
      onWin: [
        "...",
        "Deeper, then.",
        "Don't say I didn't warn you.",
      ],
      onLoss: [
        "The door remains closed to those who don't belong here.",
        "Come back when you've earned it.",
      ],
    },
    rewardCrests: 1,
  },

  {
    id: "sovrn",
    name: "Sov'rn",
    title: "The Archivist",
    innerWinsRequired: 3,
    difficulty: "Champion",
    starterDeckId: "starter_z4z",
    avatarIcon: "⏳",
    accentColor: "#22d3ee",
    lore: "Sov'rn has catalogued every Aenarchist who reached this corridor. The records go back further than the Plaza itself. At some point, Sov'rn stopped noting whether they survived — only whether they were interesting. The distinction matters less than it used to.",
    dialogue: {
      approach: [
        "I've recorded every Aenarchist who reached this corridor.",
        "Most of them are in the past tense now.",
        "Let's see which column you belong in.",
      ],
      onWin: [
        "Interesting.",
        "You'll get your own entry.",
        "It will be a long one.",
      ],
      onLoss: [
        "Another entry. Another past tense.",
        "The archive grows.",
      ],
    },
    rewardCrests: 1,
  },

  {
    id: "ruk",
    name: "Rük",
    title: "The Hunger",
    innerWinsRequired: 7,
    difficulty: "Lord",
    starterDeckId: "starter_chaos_dragon",
    avatarIcon: "🌑",
    accentColor: "#dc2626",
    lore: "No one knows where Rük came from. No one has asked. The Chaos Dragon that accompanies it arrived with it, and neither has explained themselves. Rük does not negotiate. Rük does not taunt. Rük simply arrives, and the field changes.",
    dialogue: {
      approach: [
        "...",
      ],
      onWin: [
        "...",
      ],
      onLoss: [
        "...",
      ],
    },
    rewardCrests: 1,
  },

  {
    id: "keldara",
    name: "Kel'dara",
    title: "The Warden of the Deep",
    innerWinsRequired: 15,
    difficulty: "Lord",
    starterDeckId: "starter_h01",
    avatarIcon: "✦",
    accentColor: "#6d28d9",
    lore: "Kel'dara was the first being the Inner Minerium produced on its own — not summoned, not assigned, not wandered in. The Minerium made Kel'dara from what previous Aenarchists left behind: their strategies, their failures, their almost-victories. To fight Kel'dara is to fight everyone who came before you.",
    dialogue: {
      approach: [
        "I know your deck.",
        "I know your tendencies.",
        "I know the move you make when you're afraid.",
        "Show me something new.",
      ],
      onWin: [
        "...",
        "Something new.",
        "Good.",
        "Keep going.",
      ],
      onLoss: [
        "Predictable.",
        "Come back when you've surprised yourself.",
      ],
    },
    rewardCrests: 1,
  },

  {
    id: "the_deep",
    name: "???",
    title: "???",
    innerWinsRequired: 30,
    difficulty: "Lord",
    starterDeckId: "starter_chaos_dragon",
    avatarIcon: "◆",
    accentColor: "#ff2266",
    lore: "???",
    locked: true,
    dialogue: {
      approach: [ "..." ],
      onWin:    [ "..." ],
      onLoss:   [ "..." ],
    },
    rewardCrests: 1,
  },
];

// ⚠ DECLARED HERE AND NOT BESIDE MINERIUM_BY_ID, WHICH IS WHERE IT
// FIRST WENT. MINERIUM_BY_ID sits ABOVE INNER_VILLAINS in this file, so
// `INNER_VILLAINS.filter(...)` up there ran before the const it reads
// was initialised — a temporal dead zone the parser accepts happily and
// the browser answers with "Cannot access 'INNER_VILLAINS' before
// initialization", taking the whole module graph down with it. A
// derived list must live under the thing it derives from.
//
// The rungs a player may actually climb. See `storyOnly` in the banner
// above — three of the eight belong to Story Mode and are filtered out
// HERE, once, rather than in whichever screen happens to render a list.
// A screen that wants the whole cast still has INNER_VILLAINS.
export const LADDER_VILLAINS = INNER_VILLAINS.filter(v => !v.storyOnly);

// ⚠ TEST PRICING, 2026-08-19. caP'n: "honestly just make every
// single item in the shop 1 xD". Every non-zero price on this shelf
// is 1 so the whole economy can be walked end to end in an evening.
// A zero stays a zero -- zero means "already yours", not "free".
// THIS IS ONE COMMIT. `git revert` it and the real numbers return.
export const BOOSTER_PACKS = [
  // ═══════════════════════════════════════════════════════════════
  // ◆ CORE ARCHETYPES — The Foundational Five ◆
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 1: Void Eclipse (VE) ────────────────────────────────
  {
    id: "void_eclipse",
    coverCardId: "spectral_tiger_lord",
    name: "Void Eclipse",
    subtitle: "Phantom Beings · Nihil · Ritual",
    description: "Spectral Tiger, Shadow Fox, Inverted Serpent. All 9 Nihil Tomes and all 9 Ritual Tomes. Brooding, strategic, slow-burn.",
    flavor: "◆ Your shadow already belongs to the dark. ◆",
    icon: "◇",
    code: "VE",
    gradient: "linear-gradient(135deg, #1a0a2e, #0a0015)",
    accentColor: "#7c3aed",
    glowColor: "#7c3aed55",
    wrapperColor: "Purple + Black",
    gold: "VE-039G — Haunting Presence (Spectral Tiger Lord U'hlti)",
    mythic: "VE-033M — X'aol (Evil Affinity — most feared card in the game)",
    cost: 1,
    signatureTier: "holo_khao",
  },
  // ── Pack 2: Solar Ascension (SA) ─────────────────────────────
  {
    id: "solar_ascension",
    coverCardId: "h01_lord",
    name: "Solar Ascension",
    subtitle: "Space Beings · Construction · Treasure",
    description: "H-01, H-09, Xulan. All 9 Construction Tomes and all 9 Treasure Tomes. Powerful, aggressive, high-Lyt energy.",
    flavor: "◆ The stars bend to their will. ◆",
    icon: "✦",
    code: "SA",
    gradient: "linear-gradient(135deg, #1e3a5f, #0a1a3a)",
    accentColor: "#3b82f6",
    glowColor: "#3b82f655",
    wrapperColor: "Gold foil",
    gold: "SA-039G — Show Stomper (Premier Dragon Lord U'hlti, dim Anima by 10)",
    mythic: "SA-033M — Space Race (Win condition Prophecy for Space ilk)",
    cost: 1,
    signatureTier: "holo_gold",
  },
  // ── Pack 3: Clockwork Dominion (CD) ──────────────────────────
  {
    id: "clockwork_dominion",
    coverCardId: "dg0_lord",
    name: "Clockwork Dominion",
    subtitle: "Time Beings · Omnia · Contract",
    description: "Z4Z, DG-0, Antdroid. All 9 Omnia Tomes and all 9 Contract Tomes. Control-heavy, oppressive, engine-building.",
    flavor: "◆ Time is a cog in the machine's design. ◆",
    icon: "⟳",
    code: "CD",
    gradient: "linear-gradient(135deg, #0a2020, #051010)",
    accentColor: "#0d9488",
    glowColor: "#0d948855",
    wrapperColor: "Teal + Silver foil",
    gold: "CD-039G — Sword & Shield of O'mnis (nullify any card activation)",
    mythic: "CD-033M — Time to Pretend (Win condition for 2 Supreme Time beings)",
    cost: 1,
    signatureTier: "holo_depths",
  },
  // ── Pack 4: Dragon's Wrath (DW) ───────────────────────────────
  {
    id: "dragons_wrath",
    coverCardId: "premier_dragon_lord",
    name: "Dragon's Wrath",
    subtitle: "Dragon Beings · Ancient Sorcery · Shadow Gambling",
    description: "Lucky Dragon, Premier Dragon, Chaos Dragon. All Ancient Sorcery and Shadow Gambling Tomes. Chaotic, high-variance, big swings.",
    flavor: "◆ Even fire bows before the Minerium. ◆",
    icon: "◈",
    code: "DW",
    gradient: "linear-gradient(135deg, #3a0a0a, #1a0000)",
    accentColor: "#dc2626",
    glowColor: "#dc262655",
    wrapperColor: "Purple + Red foil",
    gold: "DW-039G — Casino Royale (auto-win your next Shadow Gambling)",
    mythic: "DW-033M — Dragonforce (Win condition for 2 Supreme Dragon beings)",
    cost: 1,
    signatureTier: "holo_solrend",
  },
  // ── Pack 5: Supreme Council (SC) ─────────────────────────────
  {
    id: "supreme_council",
    coverCardId: "proph_tablet_omnis",
    name: "Supreme Council",
    subtitle: "All U'hltis · Affinities · Prophecies · Curios",
    description: "No beings. Pure game-ending power. All 12 U'hltis, all 4 Affinities, all 8 Prophecies, all 9 Curios. The prestige pack.",
    flavor: "◆ The council has spoken. The rest is ritual. ◆",
    icon: "⬡",
    code: "SC",
    gradient: "linear-gradient(135deg, #2a1a0a, #15080a)",
    accentColor: "#d4a84b",
    glowColor: "#d4a84b55",
    wrapperColor: "Rainbow holographic foil",
    gold: "SC-033G — I or None (force both players to ruin beings until 1 each remains)",
    mythic: "SC-021M — Tablet of O'mnis (Win condition: 2 Supreme Beings + D'/A'aeternis)",
    cost: 1,
    signatureTier: "holo_aeternus",
  },

  // ═══════════════════════════════════════════════════════════════
  // ◆ SUPPORT & UTILITY — The Competitive Edge ◆
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 6: Instinct Rising (IR) ─────────────────────────────
  {
    id: "instinct_rising",
    coverCardId: "chaos_dragon_lord",
    name: "Instinct Rising",
    subtitle: "All 36 Impera · Curios",
    description: "No beings. Pure support. All 36 Impera and 9 Curios. The most important competitive pack — Impera are guaranteed here.",
    flavor: "◆ Instinct was never lost — it was waiting. ◆",
    icon: "◉",
    code: "IR",
    gradient: "linear-gradient(135deg, #1a2e0a, #0a1a05)",
    accentColor: "#16a34a",
    glowColor: "#16a34a55",
    wrapperColor: "Gold + Green foil",
    gold: "IR-045G — None More Than I (beings over 10 Lyt instantly ruined)",
    mythic: "IR-036M — Bloody Hell (Win condition: any Warlock loses 45+ Anima)",
    cost: 1,
    // signatureTier: "holo_shadow" — retired July 25, 2026. Left unset so
    // the shelf aura falls back to this pack's own accentColor. Repoint it
    // at the new Shadow when one is cut.
  },
  // ── Pack 7: Unstable World (UW) ───────────────────────────────
  {
    id: "unstable_world",
    coverCardId: "aff_jynxi",
    name: "Unstable World",
    subtitle: "All L1 Beings · U'hltis · Shadow Gambling · Affinities",
    description: "Every ilk's L1 beings, all U'hltis, Shadow Gambling, Contract Tomes, all 4 Esclings, all 4 Affinities, 1 Prophecy. The chaos pack.",
    flavor: "◆ In the Unstable World, anything can happen — and does. ◆",
    icon: "✶",
    code: "UW",
    gradient: "linear-gradient(135deg, #1a0a1a, #0a0010)",
    accentColor: "#ff2266",
    glowColor: "#ff226655",
    wrapperColor: "Rainbow holographic foil",
    gold: "UW-050G — Dragon Eyes (roll double-1s to win 2 Shadow Gamblings instantly)",
    mythic: "UW-033M — Ace of Shades (win 7 Shadow Gamblings in a row, win the duel)",
    cost: 1,
    signatureTier: "holo_jynxi",
  },

  // ═══════════════════════════════════════════════════════════════
  // ✦ HOLOGRAPHIC BOUTIQUE — Limited Edition Foil Variants ✦
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 8: Void Eclipse — Mythic Holo Edition (VE-M) ────────
  {
    id: "void_eclipse_mythic",
    coverCardId: "shadow_fox_lord",
    name: "Void Eclipse — Mythic Edition",
    subtitle: "Every card pulls as ◆ MYTHIC or ◈ SILVER",
    description: "The full Void Eclipse lineup, guaranteed holographic foil on every single card. Phantom Beings, Nihil, Ritual. Each card shimmers with mythic or silver luminescence.",
    flavor: "◆ From the void, light emerges differently. ◆",
    icon: "◆",
    code: "VE-M",
    gradient: "linear-gradient(135deg, #2a1a3e, #1a0a25)",
    accentColor: "#ff66cc",
    glowColor: "#ff66cc88",
    wrapperColor: "Mythic Shimmer",
    gold: "VE-M-039 — Haunting Presence (Spectral Tiger Lord U'hlti)",
    mythic: "VE-M-033 — X'aol (Evil Affinity)",
    cost: 1,
    holoGuarantee: true,
    holoTiers: ["holo_mythic", "holo_silver"],
  },
  // ── Pack 9: Solar Ascension — Gold Holo Edition (SA-G) ────────
  {
    id: "solar_ascension_gold",
    coverCardId: "h09_lord",
    name: "Solar Ascension — Gold Edition",
    subtitle: "Every card pulls as ✦ GOLD or ◈ SILVER",
    description: "The full Solar Ascension lineup, guaranteed holographic foil. Space Beings, Construction, Treasure. Premium gold shimmer across every pull.",
    flavor: "◆ The golden light of achievement shines eternal. ◆",
    icon: "✦",
    code: "SA-G",
    gradient: "linear-gradient(135deg, #2e3a5f, #1a2a4a)",
    accentColor: "#ffd700",
    glowColor: "#ffd70088",
    wrapperColor: "Gold Foil",
    gold: "SA-G-039 — Show Stomper (Premier Dragon Lord U'hlti)",
    mythic: "SA-G-033 — Space Race (Win condition Prophecy)",
    cost: 1,
    holoGuarantee: true,
    holoTiers: ["holo_gold", "holo_silver"],
  },
  // ── Pack 10: Clockwork Dominion — Depths Holo (CD-D) ────────
  {
    id: "clockwork_dominion_depths",
    coverCardId: "z4z_lord",
    name: "Clockwork Dominion — Depths Edition",
    subtitle: "Every card pulls as ◆ DEPTHS cascade foil",
    description: "Time Beings, Omnia, Contract — every card rendered in the signature ocean-blue cascading depths effect. Strategic gameplay meets haunting visuals.",
    flavor: "◆ In the depths of time, all mysteries await. ◆",
    icon: "◆",
    code: "CD-D",
    gradient: "linear-gradient(135deg, #0a3030, #051820)",
    accentColor: "#00aaff",
    glowColor: "#00aaff88",
    wrapperColor: "Depths Cascade",
    gold: "CD-D-039 — Sword & Shield of O'mnis",
    mythic: "CD-D-033 — Time to Pretend",
    cost: 1,
    holoGuarantee: true,
    holoTiers: ["holo_depths"],
  },
  // ── Pack 11: Dragon's Wrath — Solrend Holo (DW-S) ────────────
  {
    id: "dragons_wrath_solrend",
    coverCardId: "lucky_dragon_lord",
    name: "Dragon's Wrath — Solrend Edition",
    subtitle: "Every card pulls with ◆ SOLREND crimson pulse",
    description: "Dragon Beings, Ancient Sorcery, Shadow Gambling — wrapped in the burning red Solrend foil. Chaos and fire in every pull.",
    flavor: "◆ The Solrend: where fire forgets its mercy. ◆",
    icon: "◆",
    code: "DW-S",
    gradient: "linear-gradient(135deg, #4a1a1a, #2a0a0a)",
    accentColor: "#ff2266",
    glowColor: "#ff226688",
    wrapperColor: "Solrend Pulse",
    gold: "DW-S-039 — Casino Royale",
    mythic: "DW-S-033 — Dragonforce",
    cost: 1,
    holoGuarantee: true,
    holoTiers: ["holo_solrend"],
  },
  // ── Pack 12: Supreme Council — A'eterni Holo (SC-A) ──────────
  {
    id: "supreme_council_aeterni",
    coverCardId: "proph_tablet_nullus",
    name: "Supreme Council — A'eterni Edition",
    subtitle: "Every card pulls with ✦ A'ETERNI celestial white foil",
    description: "All U'hltis, Affinities, Prophecies, Curios — the full power of the council rendered in transcendent A'eterni white. Ethereal and absolute.",
    flavor: "◆ In the A'eterni light, all truth is revealed. ◆",
    icon: "✦",
    code: "SC-A",
    gradient: "linear-gradient(135deg, #3a2a1a, #2a1a0a)",
    accentColor: "#f0f0ff",
    glowColor: "#f0f0ff66",
    wrapperColor: "A'eterni Glow",
    gold: "SC-A-033 — I or None",
    mythic: "SC-A-021 — Tablet of O'mnis",
    cost: 1,
    holoGuarantee: true,
    holoTiers: ["holo_aeternus"],
  },
  // ── Pack 13: Instinct Rising — Shadow Edition (IR-SH) ──────────
  // RETIRED FROM THE SHELF, July 25, 2026 (caP'n's ruling).
  // This pack guarantees holo_shadow on every card, and holo_shadow no
  // longer has a recipe in either foil book — selling it would ship
  // players a guaranteed foil that renders as base card. Pulled whole
  // rather than repointed at Khao: doctrine §5 forbids substituting one
  // tier for another, and a "Shadow Edition" full of Khao foils is a
  // lie on the wrapper. Uncomment and retune when the new Shadow ships.
  // // ── Pack 13: Instinct Rising — Shadow Holo (IR-SH) ──────────
  // {
  //   id: "instinct_rising_shadow",
  //   name: "Instinct Rising — Shadow Edition",
  //   subtitle: "Every card pulls with ◇ SHADOW invert foil",
  //   description: "All 36 Impera and 9 Curios — the support deck rendered in mysterious Shadow foil. Every card carries hidden depth.",
  //   flavor: "◆ Instinct whispers from the shadow. ◆",
  //   icon: "◇",
  //   code: "IR-SH",
  //   gradient: "linear-gradient(135deg, #2a1a3a, #1a0a2a)",
  //   accentColor: "#9966ff",
  //   glowColor: "#9966ff77",
  //   wrapperColor: "Shadow Invert",
  //   gold: "IR-SH-045 — None More Than I",
  //   mythic: "IR-SH-036 — Bloody Hell",
  //   cost: 13,
  //   holoGuarantee: true,
  //   holoTiers: ["holo_shadow"],
  // },
  // ── Pack 14: Void Eclipse — Void Holo (VE-V) ─────────────────
  {
    // KHAO RENAME (July 25, 2026): display text only. `id` and `code`
    // stay "void_eclipse_void" / "VE-V" — stored purchases point at them.
    id: "void_eclipse_void",
    coverCardId: "inverted_serpent_lord",
    name: "Void Eclipse — Khao Edition",
    subtitle: "Every card pulls with ◈ KHAO deep purple pulse",
    description: "Phantom Beings, Nihil, Ritual — each card rendered in the roiling Khao foil. Darkness upon darkness, perfectly synergized.",
    flavor: "◆ When Khao calls to Khao, all else fades. ◆",
    icon: "◈",
    code: "VE-V",
    gradient: "linear-gradient(135deg, #3a0a5e, #1a0a3e)",
    accentColor: "#9333ea",
    glowColor: "#9333ea88",
    wrapperColor: "Khao Pulse",
    gold: "VE-V-039 — Haunting Presence",
    mythic: "VE-V-033 — X'aol",
    cost: 1,
    holoGuarantee: true,
    holoTiers: ["holo_khao"],
  },
  // ── Pack 14b: Unstable World — J'ynxi Edition (UW-J) ─────────
  {
    id: "unstable_world_jynxi",
    coverCardId: "proph_ace_of_shades",
    name: "Unstable World — J'ynxi Edition",
    subtitle: "Every card pulls with ✦ J'YNXI emerald grin",
    description: "The full chaos lineup, every card wrapped in the trickster's green. L1 beings, U'hltis, Shadow Gambling, Affinities — Fiend or Faux, all of it foiled.",
    flavor: "◆ The green that grins back. Friend or faux? ◆",
    icon: "✦",
    code: "UW-J",
    gradient: "linear-gradient(135deg, #0a3a1a, #052a12)",
    accentColor: "#2ee66b",
    glowColor: "#2ee66b88",
    wrapperColor: "J'ynxi Emerald",
    gold: "UW-J-050 — Dragon Eyes",
    mythic: "UW-J-033 — Ace of Shades",
    cost: 1,
    holoGuarantee: true,
    holoTiers: ["holo_jynxi"],
    signatureTier: "holo_jynxi",
  },

  // ═══════════════════════════════════════════════════════════════
  // ◉ MONO-SCHOOL POWER — Dedicated Pursuit ◉
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 15: Space Ascendants (SP) ────────────────────────────
  {
    id: "space_ascendants",
    coverCardId: "u_h01_lord",
    name: "Space Ascendants",
    subtitle: "H-01, H-09, Xulan · All Space ilk Tomes",
    description: "Focused on Space beings from every lineage. Construction, Treasure, Shadow Gambling. Build the ultimate Space deck.",
    flavor: "◆ In space, they are gods. ◆",
    icon: "★",
    code: "SP",
    gradient: "linear-gradient(135deg, #0a1a4a, #051030)",
    accentColor: "#60a5fa",
    glowColor: "#60a5fa55",
    wrapperColor: "Starfield foil",
    gold: "SP-009G — Cosmic Burst (all Space beings +3 Lyt this turn)",
    mythic: "SP-021M — Supernova Protocol",
    cost: 1,
  },
  // ── Pack 16: Phantom Mastery (PH) ────────────────────────────
  {
    id: "phantom_mastery",
    coverCardId: "u_spectral_tiger_lord",
    name: "Phantom Mastery",
    subtitle: "Spectral Tiger, Shadow Fox, Inverted Serpent · Phantom focus",
    description: "Deep dive into Phantom ilk. Nihil synergies, Ritual power, Ancient Sorcery. Master the unseen.",
    flavor: "◆ In the phantom's realm, you are never alone. ◆",
    icon: "◇",
    code: "PH",
    gradient: "linear-gradient(135deg, #1a0a2e, #0a0020)",
    accentColor: "#7c3aed",
    glowColor: "#7c3aed55",
    wrapperColor: "Ethereal shimmer",
    gold: "PH-012G — Phantom Echo (beings ruined cost opponent 5 Anima)",
    mythic: "PH-018M — Spectral Ascension",
    cost: 1,
  },
  // ── Pack 17: Time Engine (TM) ────────────────────────────────
  {
    id: "time_engine",
    coverCardId: "u_z4z_lord",
    name: "Time Engine",
    subtitle: "Z4Z, DG-0, Antdroid · Time control mastery",
    description: "Time beings, Omnia contracts, Shadow Gambling for clockwork precision. Build the perfect engine.",
    flavor: "◆ Time turns at your command. ◆",
    icon: "⟳",
    code: "TM",
    gradient: "linear-gradient(135deg, #0a2a2a, #051515)",
    accentColor: "#14b8a6",
    glowColor: "#14b8a655",
    wrapperColor: "Teal mechanism",
    gold: "TM-008G — Temporal Loop (repeat your last Tome effect)",
    mythic: "TM-015M — Eternity Lock",
    cost: 1,
  },
  // ── Pack 18: Dragon's Armory (DR) ───────────────────────────
  {
    id: "dragons_armory",
    coverCardId: "u_premier_dragon_lord",
    name: "Dragon's Armory",
    subtitle: "Lucky, Premier, Chaos Dragons · Dragon focused",
    description: "All three Dragon lines, Ancient Sorcery, Shadow Gambling. Harness draconic chaos and power.",
    flavor: "◆ Dragons fear no arsenal. ◆",
    icon: "◆",
    code: "DR",
    gradient: "linear-gradient(135deg, #4a1a0a, #2a0a00)",
    accentColor: "#f97316",
    glowColor: "#f9731655",
    wrapperColor: "Dragon scale",
    gold: "DR-011G — Breath of the Emperium (+8 Lyt to 1 Dragon)",
    mythic: "DR-019M — Ancient Flight",
    cost: 1,
  },

  // ═══════════════════════════════════════════════════════════════
  // ◈ TOME MASTERY — Spellcraft Focus ◈
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 19: Shadow Gambler's Fortune (SG) ───────────────────
  {
    id: "shadow_gambler",
    coverCardId: "tome_casino_royale",
    name: "Shadow Gambler's Fortune",
    subtitle: "Three of the 9 Shadow Gambling Tomes · Chance & chaos",
    description: "Three Tomes drawn from the whole Shadow Gambling school. Maximize variance and risk-reward. Roll the dice.",
    flavor: "◆ Fortune favors the bold — and the lucky. ◆",
    icon: "◲",
    code: "SG",
    gradient: "linear-gradient(135deg, #2a1a0a, #1a0a05)",
    accentColor: "#f59e0b",
    glowColor: "#f59e0b55",
    wrapperColor: "Velvet + gold",
    gold: "SG-006G — The House Always Loses (2 Shadow Gamblings auto-win)",
    mythic: "SG-012M — Jackpot Infinite",
    cost: 1,
  },
  // ── Pack 20: Ritual's Ascent (RT) ────────────────────────────
  {
    id: "rituals_ascent",
    coverCardId: "tome_seal_of_omnis",
    name: "Ritual's Ascent",
    subtitle: "Three of the 9 Ritual Tomes · Resurrection engine",
    description: "Three drawn from the nine Ritual Tomes — Seals, Monoliths and Mausoleums of Omnis, Nihil and the Soulcerer. Resurrect and reclaim.",
    flavor: "◆ Death yields to ritual. ◆",
    icon: "⚱",
    code: "RT",
    gradient: "linear-gradient(135deg, #1a0a2a, #0a0015)",
    accentColor: "#a855f7",
    glowColor: "#a855f755",
    wrapperColor: "Mortuary purple",
    gold: "RT-007G — Soulcerer's Mausoleum (resurrect 2 beings)",
    mythic: "RT-013M — Eternal Return",
    cost: 1,
  },
  // ── Pack 21: Nihil's Embrace (NL) ───────────────────────────
  {
    id: "nihils_embrace",
    coverCardId: "tome_sword_shield_nihil",
    name: "Nihil's Embrace",
    subtitle: "Three of the 9 Nihil Tomes · Void synergy",
    description: "Three drawn from the Nihil school. Rays of Hope through the Sword and Shield of Nihil. Cleansing light, wielded by the void.",
    flavor: "◆ In nihil, all debts are paid. ◆",
    icon: "◈",
    code: "NL",
    gradient: "linear-gradient(135deg, #0a0a1a, #050510)",
    accentColor: "#8b5cf6",
    glowColor: "#8b5cf655",
    wrapperColor: "Deep purple",
    gold: "NL-008G — Void Ascension (all beings +2 Lyf this turn)",
    mythic: "NL-014M — Nihil Eternal",
    cost: 1,
  },
  // ── Pack 22: Contract Broker (CB) ───────────────────────────
  {
    id: "contract_broker",
    coverCardId: "tome_devils_advocate",
    name: "Contract Broker",
    subtitle: "Three of the 9 Contract Tomes · Social engineering",
    description: "Three drawn from Devil's Advocate through King of the Castle. Negotiate your way to victory.",
    flavor: "◆ A contract is only binding if both sides believe it. ◆",
    icon: "⚖",
    code: "CB",
    gradient: "linear-gradient(135deg, #2a1a0a, #1a0a05)",
    accentColor: "#d97706",
    glowColor: "#d9770655",
    wrapperColor: "Parchment + ink",
    gold: "CB-009G — Airtight Agreement (lock opponent's Khao)",
    mythic: "CB-015M — Unbreakable Bond",
    cost: 1,
  },


  // ═══════════════════════════════════════════════════════════════
  // ⭐ THE OTHER FOUR SCHOOLS (caP'n, 2026-08-22): *"missing a lot of
  // Tome packs — we have Nihil but no Omnia?"*
  // ═══════════════════════════════════════════════════════════════
  // There are EXACTLY EIGHT Tome ilks in cards.js — Construction,
  // Treasure, Nihil, Omnia, Ancient Sorcery, Ritual, Shadow Gambling,
  // Contract — and EXACTLY NINE Tomes in each, 72 total. Four had a
  // pack and four did not, which is why Omnia was unbuyable as a
  // school. The shelf is now one pack per ilk, no ilk twice, none
  // missing. ⚠ If a ninth Tome ilk is ever written, it needs a pack
  // here, a pool in progression.js, a price row, and a sigil — four
  // places, and the shelf is the only one that shows the gap.

  // ── Pack 23: The Architect's Codex (CN) — Construction ───────
  {
    id: "architects_codex",
    coverCardId: "tome_stars_and_skies",
    name: "The Architect's Codex",
    subtitle: "Three of the 9 Construction Tomes · Khao & Anima engine",
    description: "Three drawn from the Construction school — Khao Gardens, Arcadias, Paradises, the D'uhmdumn line and The Stars and Skies. Build the board before you fight on it.",
    flavor: "◆ Every empire was a foundation first. ◆",
    icon: "⌂",
    code: "CN",
    gradient: "linear-gradient(135deg, #0a2a1a, #05150c)",
    accentColor: "#34d399",
    glowColor: "#34d39955",
    wrapperColor: "Jade + mortar",
    gold: "CN-007G — The Stars and Skies (+5 Anima)",
    mythic: "CN-013M — Khao Eternal",
    cost: 1,
  },
  // ── Pack 24: The Treasure Hoard (TR) — Treasure ──────────────
  {
    id: "treasure_hoard",
    coverCardId: "tome_necklace_akula",
    name: "The Treasure Hoard",
    subtitle: "Three of the 9 Treasure Tomes · Arms & regalia",
    description: "Three drawn from the Treasure school. Necklaces, blades, gauntlets, boots and Chief Calidum's Cuirass. What a being carries is half of what it is.",
    flavor: "◆ Steel remembers who held it. ◆",
    icon: "⚱",
    code: "TR",
    gradient: "linear-gradient(135deg, #2a2008, #150f03)",
    accentColor: "#eab308",
    glowColor: "#eab30855",
    wrapperColor: "Beaten gold",
    gold: "TR-006G — Necklace of Akula",
    mythic: "TR-012M — Weeping Dagger",
    cost: 1,
  },
  // ── Pack 25: Omnia's Dominion (OM) — Omnia ───────────────────
  {
    id: "omnias_dominion",
    coverCardId: "tome_sword_shield_omnis",
    name: "Omnia's Dominion",
    subtitle: "Three of the 9 Omnia Tomes · Everything, held at once",
    description: "Three drawn from the Omnia school. Regnant, Superque, All or Nothing, Omnis' Corruption Jet and the Sword and Shield of O'mnis. The counterweight to Nihil.",
    flavor: "◆ Nihil takes. Omnia keeps. ◆",
    icon: "◎",
    code: "OM",
    gradient: "linear-gradient(135deg, #1a1030, #0a0518)",
    accentColor: "#c084fc",
    glowColor: "#c084fc55",
    wrapperColor: "Violet + silver",
    gold: "OM-008G — Sword and Shield of O'mnis",
    mythic: "OM-014M — All or Nothing",
    cost: 1,
  },
  // ── Pack 26: Ancient Sorcery (AS) — Ancient Sorcery ──────────
  {
    id: "ancient_sorcery",
    coverCardId: "tome_river_of_blood",
    name: "Ancient Sorcery",
    subtitle: "Three of the 9 Ancient Sorcery Tomes · Blood & absence",
    description: "Three drawn from the oldest school. Not Here, Not Now, Not Ever, the Blood line, and the Shadow Blanket. The oldest arts, and the least polite.",
    flavor: "◆ Older than the Tomes that wrote it down. ◆",
    icon: "☾",
    code: "AS",
    gradient: "linear-gradient(135deg, #2a0a12, #150408)",
    accentColor: "#ef4444",
    glowColor: "#ef444455",
    wrapperColor: "Dried blood + ash",
    gold: "AS-009G — River of Blood",
    mythic: "AS-015M — Not Ever!",
    cost: 1,
  },

  // ═══════════════════════════════════════════════════════════════
  // ✦ PREMIER EXPERIENCES — The Collector's Cabinet ✦
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 23: All-Stars Mythic Bundle (ASM) ──────────────────
  {
    id: "all_stars_mythic",
    coverCardId: "xulan_lord",
    name: "All-Stars — Mythic Bundle",
    subtitle: "Every archetype's best · Guaranteed ◆ MYTHIC each",
    description: "One curated card from each archetype (all 12 U'hltis included), guaranteed to pull as MYTHIC holographic. Build your dream deck of legends.",
    flavor: "◆ The greatest collection ever assembled. ◆",
    icon: "◆",
    code: "ASM",
    gradient: "linear-gradient(135deg, #3a2a1a, #2a1a0a)",
    accentColor: "#ff66cc",
    glowColor: "#ff66cc88",
    wrapperColor: "Mythic rainbow",
    gold: "ASM-001 — Your choice mythic",
    mythic: "ASM-001M — Rare pulled as pure mythic",
    cost: 1,
    holoGuarantee: true,
    holoTiers: ["holo_mythic"],
  },
  // ── Pack 24: Depths Sanctuary (DS) ────────────────────────────
  {
    id: "depths_sanctuary",
    coverCardId: "u_inverted_serpent_lord",
    name: "Depths Sanctuary",
    subtitle: "Complete Endless Depths replica pack",
    description: "Recreate the Gatekeeper's collection. Every card from every trophy tier, guaranteed ◆ DEPTHS foil. The full ocean cascade experience.",
    flavor: "◆ Dive deep. The Depths remember you. ◆",
    icon: "◆",
    code: "DS",
    gradient: "linear-gradient(135deg, #0a2a3a, #051520)",
    accentColor: "#00aaff",
    glowColor: "#00aaff88",
    wrapperColor: "Ocean cascade",
    gold: "DS-042 — Depths pull",
    mythic: "DS-042D — Depths rare pull",
    cost: 1,
    holoGuarantee: true,
    holoTiers: ["holo_depths"],
  },
  // ── Pack 25: Hybrid Chaos (HC) ───────────────────────────────
  {
    id: "hybrid_chaos",
    coverCardId: "aff_evil",
    name: "Hybrid Chaos",
    subtitle: "Cross-ilk power · Mixed rarity holos",
    description: "Beings from two different ilks per pull + support cards. May the chaos guide you. Mixed holo tiers make each pull unique.",
    flavor: "◆ Where ilks converge, chaos blooms. ◆",
    icon: "✶",
    code: "HC",
    gradient: "linear-gradient(135deg, #2a1a2a, #1a0a1a)",
    accentColor: "#ec4899",
    glowColor: "#ec489955",
    wrapperColor: "Fractured prism",
    gold: "HC-033G — Collision Event (1 being from each ilk)",
    mythic: "HC-027M — Dimensional Breach",
    cost: 1,
  },
  // ── Pack 26: Affinity Ascension (AA) ──────────────────────────
  {
    id: "affinity_ascension",
    coverCardId: "aff_awoken",
    name: "Affinity Ascension",
    subtitle: "All 4 Affinities · Supreme alignment",
    // The four PRINTED names, off cards.js. Three of the four were wrong
    // here: the card is Y'Tyranuz (no z), J'ynxi's Affinity is called
    // Fiend or Faux, and the Holy one is Enlightenment.
    description: "Every Affinity (X'aol, Y'Tyranuz, Fiend or Faux, Enlightenment). Build your ideological empire.",
    flavor: "◆ Choose your path. One affinity defines all. ◆",
    icon: "◉",
    code: "AA",
    gradient: "linear-gradient(135deg, #1a2a1a, #0a1a0a)",
    accentColor: "#10b981",
    glowColor: "#10b98155",
    wrapperColor: "Harmonic alignment",
    gold: "AA-004G — Enlightened Harmony (all ilks +2 Lyt)",
    mythic: "AA-008M — Supreme Ascension",
    cost: 1,
  },
  // ── Pack 27: Prophecy Oracle (PO) ────────────────────────────
  {
    id: "prophecy_oracle",
    coverCardId: "proph_space_race",
    name: "Prophecy Oracle",
    subtitle: "All 8 Prophecies + curios",
    description: "Every win condition card. See all possible futures. Complete your collection of destiny.",
    flavor: "◆ The oracle sees all — which path will you choose? ◆",
    icon: "🔮",
    code: "PO",
    gradient: "linear-gradient(135deg, #2a0a3a, #1a0a2a)",
    accentColor: "#a78bfa",
    glowColor: "#a78bfa55",
    wrapperColor: "Crystalline glow",
    gold: "PO-005G — Vision Quest (draw cards, see your future)",
    mythic: "PO-016M — Infinite Sight",
    cost: 1,
  },

  // ═══════════════════════════════════════════════════════════════
  // ◆ INFINITE VARIETY — The Testing Playground ◆
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 28: Randomizer Supreme (RS) ──────────────────────────
  {
    id: "randomizer_supreme",
    coverCardId: "proph_bloody_hell",
    name: "Randomizer Supreme",
    subtitle: "Any 10 cards from entire card pool · Pure chaos",
    description: "Pull 10 completely random cards from every card type. No restrictions. No focus. Pure unpredictable Terror.",
    flavor: "◆ Chaos is the only true order. ◆",
    icon: "✶",
    code: "RS",
    gradient: "linear-gradient(135deg, #1a1a2a, #0a0a1a)",
    accentColor: "#f472b6",
    glowColor: "#f472b655",
    wrapperColor: "Quantum uncertainty",
    gold: "RS-Random — Completely random pull",
    mythic: "RS-Random — Anything can be mythic",
    cost: 1,
  },
  // ═══════════════════════════════════════════════════════════════
  // ◈ SUPPORT — the cards a deck is built AROUND ◈
  // ═══════════════════════════════════════════════════════════════
  // ⭐ caP'n, 2026-08-22: *"Class and Tomes — does it have U'hltis
  // affinities Curios etc? if not then we should make 1 more section
  // for those and bundle them as Support and throw every other card
  // you forgot in there xD"*
  //
  // Audited before writing it. The School and Tome packs DO reach
  // U'hltis, Affinities and Prophecies — but NOT Curios, which lived
  // only in the pools behind the Core and Premier shelves, both of
  // which are being deleted. Without this pack the nine Curios and the
  // four Esclings become unbuyable the moment those shelves come down,
  // silently. So Support is a real hole being filled, not a fourth
  // flavour of the same thing.
  {
    id: "support_relics",
    coverCardId: "u_xulan_lord",
    name: "The Support Case",
    subtitle: "Curios · U'hltis · Affinities · Prophecies",
    description: "Four cards, and the only pack that carries Curios. Drawn from every U'hlti, every Affinity and every Prophecy — the cards a deck is built around rather than built out of.",
    flavor: "◈ A being is what you bring. This is what you bring it FOR. ◈",
    icon: "◈",
    code: "SUP",
    gradient: "linear-gradient(135deg, #14213a, #05070f)",
    accentColor: "#38bdf8",
    glowColor: "#38bdf855",
    wrapperColor: "Deep blue + steel",
    gold: "SUP-012G — Any of the twelve U'hltis",
    mythic: "SUP-Proph — Any of the eight Prophecies",
    cost: 1,
  },

  // ═══════════════════════════════════════════════════════════════
  // ⚔ ACTIVITIES — one shelf per thing you can go and do ⚔
  // ═══════════════════════════════════════════════════════════════
  // ⭐ caP'n, 2026-08-22: *"create a booster pack for every single
  // story/activity (this means gauntlet or any other activity rewarded
  // signature foil like SHV etc) so the player can buy them. That
  // being said, SHV and elo depths are purchasable in the Foil Vault
  // yes they should be, however, they should never ever be in booster
  // packs — its either do the activity or buy the specific card you
  // want for an extreme price to bypass the activity, but no booster
  // packs."*
  //
  // ⚠ SO: NOT ONE OF THESE FOUR CARRIES A `signatureTier` OR A
  // `holoTiers`, AND NOT ONE IS IN `BOUTIQUE_PACKS`. That absence is
  // the enforcement, and it is deliberately an absence rather than a
  // flag — a flag can be set by accident and a hole in a table cannot.
  // An activity pack sells the activity's CARDS so a player who will
  // never grind it can still build the deck. The FOIL stays behind the
  // activity, or behind an extreme Vault price for the one card you
  // actually want. That is the whole rarity structure, and a pack in
  // the middle of it would dissolve it — caP'n's argument against
  // Premier, applied everywhere.
  {
    id: "activity_minerium1",
    coverCardId: "antdroid_lord",
    name: "The Colonel's Case",
    subtitle: "Minerium One · H-09 · Antdroid",
    description: "Sir H'osh Vrown's own house and his own elite, plus the Treasure his kingdom was granted with. The Griddle is NOT in here — walk the Minerium, or pay the Vault.",
    flavor: "◆ He was given a kingdom. You may have the paperwork. ◆",
    icon: "◆",
    code: "M1",
    gradient: "linear-gradient(135deg, #3a0410, #0a0206)",
    accentColor: "#c17a2e",
    glowColor: "#c17a2e55",
    wrapperColor: "Crimson + white-gold",
    gold: "M1-009G — Radiant Shower (H-09 Lord U'hlti)",
    mythic: "M1-ANT — Antdroid Lord, the Colonel's own elite",
    cost: 1,
  },
  {
    id: "activity_gauntlet",
    coverCardId: "u_dg0_lord",
    name: "The Gauntlet Case",
    subtitle: "The Gauntlet · DG-0 · Premier Dragon",
    description: "What a ladder rewards: the ones who keep standing. Shields, Premier Dragons and the Contract Tomes. The Grip is NOT in here — climb it, or pay the Vault.",
    flavor: "✦ An agreement you made with yourself, in writing. ✦",
    icon: "✦",
    code: "GNT",
    gradient: "linear-gradient(135deg, #1b2229, #05080b)",
    accentColor: "#b9c2cc",
    glowColor: "#b9c2cc55",
    wrapperColor: "Cold plate + ember",
    gold: "GNT-008G — Justice Reigns (DG-0 Lord U'hlti)",
    mythic: "GNT-PRD — Premier Dragon Lord",
    cost: 1,
  },
  {
    id: "activity_depths",
    coverCardId: "u_inverted_serpent_lord",
    name: "The Descent Case",
    subtitle: "Endless Depths · Phantom · the water Tomes",
    description: "What is down there, and what it is holding. Depths is NOT in here — the Gatekeepers pay that foil and only they do.",
    flavor: "◈ The descent ends here. It does not. ◈",
    icon: "◈",
    code: "DEP",
    gradient: "linear-gradient(135deg, #001a33, #000810)",
    accentColor: "#00aaff",
    glowColor: "#00aaff55",
    wrapperColor: "Deep water",
    gold: "DEP-006G — Inverted Fangs (Inverted Serpent Lord U'hlti)",
    mythic: "DEP-AWK — Enlightenment (Awoken Affinity)",
    cost: 1,
  },
  {
    id: "activity_factions",
    coverCardId: "u_chaos_dragon_lord",
    name: "The War Case",
    subtitle: "Faction Wars · Blades of Omnis · Swords of Nullus",
    description: "Both halves of the argument, and the Z4Z who fight for each by turns. J'ynxi is NOT in here — the Hall pays that one.",
    flavor: "✶ Two seals. One of them is lying. ✶",
    icon: "✶",
    code: "FAC",
    gradient: "linear-gradient(135deg, #14210f, #050a04)",
    accentColor: "#2ee66b",
    glowColor: "#2ee66b55",
    wrapperColor: "Green fire",
    gold: "FAC-012G — I or None (Chaos Dragon Lord U'hlti)",
    mythic: "FAC-JYX — J'ynxi Affinity",
    cost: 1,
  },

];

// ── FEATURED / LIMITED EDITION HOLOS ──────────────────────────
// Rotating limited runs with special holo effects beyond silver/gold/mythic
// Each featured card has: id, cardId, holoEffect, seriesName, seriesCode,
//   description, cost, totalPrinted, available, startDate, endDate
export const FEATURED_HOLOS = [
  {
    // KHAO RENAME: display only — `id` and `seriesCode` are the
    // print run's permanent identifiers and do not move.
    id: "feat_xaol_void",
    cardId: "aff_evil",
    holoEffect: "khao",
    seriesName: "Khao Edition",
    seriesCode: "VOID-001",
    description: "X'aol rendered in the roiling Khao — the darkness between stars made manifest.",
    flavor: "◆ Even the Khao holds its breath. ◆",
    cost: 1,
    totalPrinted: 100,
    available: 100,
    accentColor: "#3b0764",
    glowColor: "#3b076488",
  },
  {
    id: "feat_spectral_solrend",
    cardId: "spectral_tiger_lord",
    holoEffect: "solrend",
    seriesName: "Solrend Edition",
    seriesCode: "SOLR-001",
    description: "The Spectral Tiger Lord bathed in Solrend red — a being that has descended beyond rank.",
    flavor: "◆ There is no top. There is only deeper. ◆",
    cost: 1,
    totalPrinted: 100,
    available: 100,
    accentColor: "#ff2266",
    glowColor: "#ff226688",
  },
];

// ─────────────────────────────────────────────────────────────
// ◆ THE FOIL VAULT — guaranteed single holographics ◆
// ─────────────────────────────────────────────────────────────
// The boutique's centerpiece. No gambling: pick the exact card in the
// exact tier you want and it is yours. Every one of the 8 holo tiers
// has flagship singles here, spread across archetypes so the whole
// rainbow is reachable through deliberate purchase.
//
// ◆ CHARGED IN DIVINE CRESTS SINCE 2026-08-18. These numbers never
// moved — this table has been denominated in ◆ since the day it was
// written, and only the DEBIT was temporarily pointed at Chocolate
// Coins while the plumbing was proven. Against THE CREST UNIT (◆1 = a
// quarter of a focused hour) the ladder reads as 1.5h to 4.5h, and a
// mythic marquee at ◆22 is five and a half. Chase prices, as intended.
// Each card-and-tier is ONE PER ACCOUNT (THE MYTHICAL LAW) — the key is
// grindable through the Well, so the door counts.
//
// Pricing ladder (Divine Crests) by tier — chase tiers cost more:
//   silver 6 · gold 9 · khao 11 · jynxi 12 · depths 13 · solrend 13
//   aeterni 15 · mythic 18   (shadow retired July 25, 2026)
// Marquee cards (Lords, U'hltis, Affinities, Prophecies) carry a +4 premium.
//
// Each entry: { id, cardId, tier, cost, blurb }
// `tier` is a holo_* string used verbatim as holoTier on purchase.
// KHAO RENAME + SHADOW RETIREMENT (July 25, 2026). holo_shadow is gone
// from the ladder — the vault must never sell a foil with no recipe.
// holo_void is kept as a legacy price key so any stored cart/purchase
// referencing it still costs the right amount.
// ⚠ TEST PRICING, 2026-08-19. caP'n: "honestly just make every
// single item in the shop 1 xD". Every non-zero price on this shelf
// is 1 so the whole economy can be walked end to end in an evening.
// A zero stays a zero -- zero means "already yours", not "free".
// THIS IS ONE COMMIT. `git revert` it and the real numbers return.
export const FOIL_VAULT_PRICE = {
  holo_silver: 1, holo_gold: 1, holo_khao: 1,
  holo_jynxi: 1, holo_depths: 1, holo_solrend: 1, holo_aeternus: 1, holo_mythic: 1,
  holo_void: 1, // legacy alias of holo_khao
  holo_vrown: 1, // Sir H'osh Vrown's own — every new foil is Vault-purchasable, per law
  holo_gauntlet: 1, // The Gauntlet's own — same law: earn it in the mode, or buy the one card you want
};
// WAS 6/9/11/12/13/13/15/18 (+4 marquee premium).
export const FOIL_VAULT_PREMIUM = 0; // added for marquee (Lord/U'hlti/Affinity/Prophecy)

// ─────────────────────────────────────────────────────────────
// FOIL VAULT — now GENERATED, not hand-listed.
//
// The vault must contain EVERY card in the game, including cards
// that don't exist yet. So instead of typing one entry per card,
// we walk the master card list (getAllGrimoireCards) and mint a
// vault entry for each one automatically. Ship a new card in
// cards.js → it appears in the vault on next load. Zero upkeep.
//
// Each card gets a SIGNATURE tier (the foil shown by default on
// its tile). The tile's tier selector then lets a player buy that
// same card in ANY of the 9 holo tiers — so "every card in every
// foil" is true without 2,500 static tiles.
//
// Curated cards below keep their hand-written blurb + signature
// tier as OVERRIDES, so the flagships still feel special.
// ─────────────────────────────────────────────────────────────

// Card types that carry the +premium marquee treatment (the chase
// rarities: Lords, U'hltis, Affinities, Prophecies). Detected by
// the card's `type` field so new flagship cards price themselves.
const MARQUEE_TYPES = new Set(["U'hlti", "Affinity", "Prophecy"]);
const isMarqueeCard = (card) => {
  if (!card) return false;
  if (MARQUEE_TYPES.has(card.type)) return true;
  // Lords are Escendants at the top of their line (level 3).
  if (card.type === "Escendant" && card.level === 3) return true;
  return false;
};

// Pick the default foil a card shows on its tile.
//   · curated override wins
//   · marquee cards default to gold (premium presence)
//   · everything else defaults to silver (clean entry tier)
const signatureTierFor = (card, override) => {
  if (override?.tier) return override.tier;
  return isMarqueeCard(card) ? "holo_gold" : "holo_silver";
};

// Hand-curated flavor. cardId → { tier?, blurb?, marquee? }.
// These ONLY override presentation/signature tier — the card is
// always present in the vault regardless of whether it's listed.
export const FOIL_VAULT_OVERRIDES = {
  aff_evil:               { tier: "holo_mythic",   blurb: "X'aol — the most feared card in the game, in full-spectrum mythic foil." },
  premier_dragon_lord:    { tier: "holo_mythic",   blurb: "The First & Only Leader, rendered in rainbow mythic." },
  proph_dragonforce:      { tier: "holo_mythic",   blurb: "Dragonforce win-condition prophecy — mythic chase." },
  h01_lord:               { tier: "holo_gold",     blurb: "H-01 Super Nova in premium gold sheen." },
  u_premier_dragon_lord:  { tier: "holo_gold",     blurb: "Show Stomper U'hlti — gilded." },
  proph_space_race:       { tier: "holo_gold",     blurb: "Space Race win-condition prophecy in gold." },
  h09_lord:               { tier: "holo_vrown",    blurb: "H-09 Hyper Nova — Sir H'osh Vrown's own house, in the Griddle." },
  u_h09_lord:             { tier: "holo_vrown",    blurb: "Radiant Shower U'hlti, white-gold to brown." },
  antdroid_lord:          { tier: "holo_vrown",    blurb: "Antdroid Lord — the Colonel's own elite, earned only at 100%." },
  chaos_dragon_lord:      { tier: "holo_solrend",  blurb: "Chaos Dragon Lord — fire that forgets mercy." },
  curio_circle_of_lyf:    { tier: "holo_silver",   blurb: "Circle of Lyf curio, silver-foiled." },
  inverted_serpent_lord:  { tier: "holo_khao",     blurb: "Inverted Serpent Lord — Khao-touched." },
  proph_phantom_planet:   { tier: "holo_khao",     blurb: "Phantom Planet prophecy in Khao foil." },
  // SHADOW RETIRED July 25, 2026 — these three marquee singles sold a
  // foil that no longer has a recipe. Pulled rather than repointed:
  // "Shadow Fox Lord in its namesake invert foil" is only true while
  // the Shadow foil exists. Restore verbatim when the new one ships.
  // shadow_fox_lord:        { tier: "holo_shadow",   blurb: "Shadow Fox Lord in its namesake invert foil." },
  // u_shadow_fox_lord:      { tier: "holo_shadow",   blurb: "Shadow Fox U'hlti — shadow-wrapped." },
  // proph_bloody_hell:      { tier: "holo_shadow",   blurb: "Bloody Hell win-condition in shadow invert." },
  z4z_lord:               { tier: "holo_depths",   blurb: "Z4Z Lord in the ocean-blue Depths cascade." },
  proph_time_to_pretend:  { tier: "holo_depths",   blurb: "Time to Pretend prophecy — Depths edition." },
  dg0_lord:               { tier: "holo_depths",   blurb: "DG-0 Lord wrapped in cascading depths." },
  spectral_tiger_lord:    { tier: "holo_solrend",  blurb: "Spectral Tiger Lord burning in Solrend crimson." },
  proph_ace_of_shades:    { tier: "holo_solrend",  blurb: "Ace of Shades prophecy in Solrend red." },
  proph_tablet_omnis:     { tier: "holo_aeternus", blurb: "Tablet of O'mnis — the ultimate win-condition, in A'eterni white." },
  xulan_lord:             { tier: "holo_aeternus", blurb: "Xulan Lord ascended in celestial white." },
  aff_awoken:             { tier: "holo_aeternus", blurb: "The Awoken affinity, rendered transcendent." },
  aff_jynxi:              { tier: "holo_jynxi",    blurb: "Fiend or Faux — the J'ynxi affinity in its own grinning green." },
  curio_jynxi_wares:      { tier: "holo_jynxi",    blurb: "J'ynxi's Wares curio, wrapped in trickster emerald." },
  lucky_dragon_lord:      { tier: "holo_jynxi",    blurb: "Lucky Dragon Lord — fortune's green smile." },
};

// Build the full vault from the master card list.
let _foilVaultCache = null;
export const buildFoilVault = (allCards) => {
  if (!Array.isArray(allCards)) return [];
  return allCards
    .filter((c) => c && c.id && c.name)
    .map((card) => {
      const override = FOIL_VAULT_OVERRIDES[card.id] || null;
      const marquee = isMarqueeCard(card);
      const tier = signatureTierFor(card, override);
      return {
        id: `fv_${card.id}`,
        cardId: card.id,
        cardName: card.name,
        cardType: card.type,
        tier,                       // signature/default foil for the tile
        marquee,
        curated: !!override,        // flagged so the gallery can spotlight it
        blurb: override?.blurb || null,
      };
    });
};

// The live vault. Generated once from getAllGrimoireCards.
export const getFoilVault = () => {
  if (_foilVaultCache) return _foilVaultCache;
  _foilVaultCache = buildFoilVault(getAllGrimoireCards());
  return _foilVaultCache;
};

// Back-compat: existing imports of FOIL_VAULT keep working. This is
// the generated list, evaluated at module load.
export const FOIL_VAULT = getFoilVault();

// Resolve the price for a vault entry in a GIVEN tier. The tier may
// be the entry's signature tier or any tier the player selected on
// the tile, so price is always computed from (tier + marquee).
export const getFoilVaultCost = (entry, tierOverride) => {
  const tier = tierOverride || entry?.tier;
  const base = FOIL_VAULT_PRICE[tier] ?? 10;
  return base + (entry?.marquee ? FOIL_VAULT_PREMIUM : 0);
};

// ─────────────────────────────────────────────────────────────
// STARTER DECK DESCRIPTIONS (for Story Mode selection)
// ─────────────────────────────────────────────────────────────

export const STARTER_DECK_LORE = {
  starter_h01:              { name: "H-01 Blue Giant",       meta: "Space",   flavor: "Sons of the Suns — words are their first resort." },
  starter_h09:              { name: "H-09 Neutron Star",     meta: "Space",   flavor: "Generals of the Stars — militancy is their first option." },
  starter_xulan:            { name: "Xulan Wanderer",        meta: "Space",   flavor: "Assassins who drift through the Void, seeking revenge." },
  starter_spectral_tiger:   { name: "Spectral Tiger",        meta: "Phantom", flavor: "Spoiled kittens of the Spirit Worlds, now hunting." },
  starter_shadow_fox:       { name: "Shadow Fox",            meta: "Phantom", flavor: "Born at the Bottomless Depths of the darkest Shadow Spheres." },
  starter_inverted_serpent: { name: "Inverted Serpent",      meta: "Phantom", flavor: "Protectors of the Shadow Forest's entrance." },
  starter_z4z:              { name: "Z4Z Demon",             meta: "Time",    flavor: "Responsible for destroying 1% of the Unstable Worlds." },
  starter_dg0:              { name: "DG-0 Angel",            meta: "Time",    flavor: "Through tremendous effort, healed 1% of dying beings." },
  starter_antdroid:         { name: "Antdroid",              meta: "Time",    flavor: "The most common robot Beb — it only seeks Anima." },
  starter_lucky_dragon:     { name: "Lucky Dragon",          meta: "Dragon",  flavor: "Rarely seen, born in the Unstable World with a fondness for Shade." },
  starter_premier_dragon:   { name: "Premier Dragon",        meta: "Dragon",  flavor: "The First & Only Leader — when born, Bloom touches the surrounding area." },
  starter_chaos_dragon:     { name: "Chaos Dragon",          meta: "Dragon",  flavor: "The One & Only Leader — its birth brings Doom and destruction." },
};

// ═══════════════════════════════════════════════════════════════
// ENDLESS DEPTHS — PROCEDURAL OPPONENT NAME POOL
// ═══════════════════════════════════════════════════════════════
// Krush-style names: real words, cryptically spelled.
// Format follows your canonical characters (T'yrent = Tyrant,
// Z'hefir = Zephyr, X'aol = Exile, V'ael = Veil, Rük = Rook/Wreck).
//
// Each entry: { name, hidden } where `hidden` is the real word —
// used internally for naming-pattern reference, never shown in-game.
// Players sound out names and get satisfying click-of-recognition.
// ═══════════════════════════════════════════════════════════════
export const DEPTHS_OPPONENTS = {
  // Low-tier — Escling/Beb opponents (0-800 Elo)
  // Vibes: new, unprepared, hopeful, searching
  escling_beb: [
    { name: "N'vise",   hidden: "Novice"     },
    { name: "W'ndrah",  hidden: "Wanderer"   },
    { name: "S'kher",   hidden: "Seeker"     },
    { name: "T'ndher",  hidden: "Tender"     },
    { name: "H'pful",   hidden: "Hopeful"    },
    { name: "L'sot",    hidden: "Lost"       },
    { name: "Hach'lyn", hidden: "Hatchling"  },
    { name: "P'lgrym",  hidden: "Pilgrim"    },
    { name: "Y'ung",    hidden: "Young"      },
    { name: "Drf'ter",  hidden: "Drifter"    },
    { name: "R'kyi",    hidden: "Rookie"     },
    { name: "N'ubent",  hidden: "New-bent"   },
  ],

  // Mid-tier — Champion/Lord opponents (800-1700 Elo)
  // Vibes: proven, practiced, strategic, committed
  champion_lord: [
    { name: "V'gyl",      hidden: "Vigil"       },
    { name: "K'nyng",     hidden: "Cunning"     },
    { name: "Pr'vyn",     hidden: "Proven"      },
    { name: "V't'ran",    hidden: "Veteran"     },
    { name: "R'vhal",     hidden: "Rival"       },
    { name: "D'wlyst",    hidden: "Duelist"     },
    { name: "A'dept",     hidden: "Adept"       },
    { name: "M'rcur",     hidden: "Mercury"     },
    { name: "S'twart",    hidden: "Stalwart"    },
    { name: "B'nden",     hidden: "Bonded"      },
    { name: "Ōth'keep",   hidden: "Oathkeeper"  },
    { name: "Thresh'ul",  hidden: "Threshold"   },
  ],

  // High-tier — Escendant/Aenarchist opponents (1700-2800 Elo)
  // Vibes: mythic, elemental, prophetic, unstoppable
  escendant_aenarchist: [
    { name: "P'rgon",   hidden: "Paragon"     },
    { name: "T'mpest",  hidden: "Tempest"     },
    { name: "O'rakyl",  hidden: "Oracle"      },
    { name: "Inf'rno",  hidden: "Inferno"     },
    { name: "L'gyn",    hidden: "Legion"      },
    { name: "W'rath",   hidden: "Wrath"       },
    { name: "O'myn",    hidden: "Omen"        },
    { name: "An'shent", hidden: "Ancient"     },
    { name: "Her'uld",  hidden: "Herald"      },
    { name: "C'lossuz", hidden: "Colossus"    },
    { name: "Eld'rych", hidden: "Eldritch"    },
    { name: "Fyr'brand",hidden: "Firebrand"   },
  ],

  // Solrend tier (2800+, no ceiling)
  // Vibes: apex, unmade, nameless, ending
  solrend: [
    { name: "A'pyx",    hidden: "Apex"        },
    { name: "Et'rnul",  hidden: "Eternal"     },
    { name: "P'numbra", hidden: "Penumbra"    },
    { name: "Entrop'",  hidden: "Entropy"     },
    { name: "Ab'yss",   hidden: "Abyss"       },
    { name: "Nihil'ys", hidden: "Nihilist"    },
    { name: "V'dwal",   hidden: "Voidwalker"  },
    { name: "N'amles",  hidden: "Nameless"    },
    { name: "R'kning",  hidden: "Reckoning"   },
    { name: "Unm'de",   hidden: "Unmade"      },
  ],
};

// Helper — pick a name for an opponent given current Elo tier
export const pickDepthsOpponentName = (elo = 0) => {
  let pool;
  if (elo < 800)       pool = DEPTHS_OPPONENTS.escling_beb;
  else if (elo < 1700) pool = DEPTHS_OPPONENTS.champion_lord;
  else if (elo < 2800) pool = DEPTHS_OPPONENTS.escendant_aenarchist;
  else                 pool = DEPTHS_OPPONENTS.solrend;
  // Solrend past 3500 — 30% chance of nameless arrival
  if (elo >= 3500 && Math.random() < 0.3) {
    return { name: "—", hidden: null, nameless: true };
  }
  return pool[Math.floor(Math.random() * pool.length)];
};

// ═══════════════════════════════════════════════════════════════
// H30 — THE STORY CO-OP AI SEAT'S REAL DECK
// ═══════════════════════════════════════════════════════════════
// V'yen's kiosk (Plaza.js) needs seat 2's actual grimoire before it
// will create a "coop" match — the CURRENT chapter's own boss or
// villain, never a copy of either host's deck. This is sync-shaped
// (no Firebase read of its own — `profile` already carries
// `storyProgress` and `innerWins`, exactly as Dungeon1.js and
// InnerMineriumSelect already read them) but the function is left
// `async` anyway, matching getGauntletOpponentDeck's signature, so
// Plaza.js's `await` on both branches of resolveAiOpponent is
// correct no matter which mode fired.
//
// ── WHAT "CURRENT CHAPTER" MEANS ────────────────────────────────
// Two sources, walked in the order Story Mode actually offers them:
//
//   1. DUNGEONS, in authored order. The first dungeon that is
//      actually playable (`unlocked` and has a roster) and not yet
//      in `storyProgress.completedDungeons` is "the chapter you're
//      in." Its current boss is the first name on `opponents` not
//      yet in `storyProgress.defeatedOpponents` — the exact walk
//      Dungeon1.js already does with its own `defeated` array,
//      mirrored here rather than re-derived.
//
//   2. LADDER_VILLAINS (not INNER_VILLAINS — the three storyOnly
//      H'osh Vrown rows are Story Mode's own door/interception/final
//      and must not be sold early as a co-op ambush; see the banner
//      above INNER_VILLAINS). Reached only once every authored
//      dungeon is cleared or none is playable yet — today that is
//      every player past Dungeon 1, since 2–6 are unbuilt. The
//      frontier is the highest-tier ladder villain innerWins has
//      actually opened, mirroring the `open` test
//      InnerMineriumSelect already gates its own picker on.
//
// Returns null — never throws — whenever there's nothing honest to
// answer with: no resolvable current boss, or a `starterDeckId` that
// doesn't match any STARTER_DECKS row. Plaza.js's stub already treats
// null as "refuse to seat this AI and say so."
export async function getStoryCoopOpponentDeck(profile) {
  const sp = profile?.storyProgress || {};
  const completed = sp.completedDungeons || [];
  const defeated  = sp.defeatedOpponents || [];

  const dungeon = DUNGEONS.find(
    (d) => d.unlocked && Array.isArray(d.opponents) && d.opponents.length && !completed.includes(d.id)
  );

  let opponent = null;
  if (dungeon) {
    // The chapter's current boss — first roster name not yet beaten.
    // Falls to the LAST name (the chapter's own boss, per OPPONENTS'
    // `isBoss` convention) if every listed foe is somehow already
    // defeated but the dungeon isn't stamped complete yet — a rare
    // race between winning and the completedDungeons write landing,
    // not a reason to hand back nothing.
    const nextId = dungeon.opponents.find((id) => !defeated.includes(id))
                || dungeon.opponents[dungeon.opponents.length - 1];
    opponent = OPPONENTS[nextId] || null;
  }

  if (!opponent) {
    // Every authored dungeon is cleared, or none is playable yet —
    // the Inner Emperium's villain ladder is Story Mode's other
    // source of a "current" foe. `locked` rows (author says "not
    // yet", independent of innerWins — see the_deep in INNER_VILLAINS)
    // are excluded the same way InnerMineriumSelect excludes them.
    const innerWins = profile?.innerWins || 0;
    const open = LADDER_VILLAINS.filter((v) => !v.locked && innerWins >= (v.innerWinsRequired || 0));
    opponent = open[open.length - 1] || null;
  }

  if (!opponent || !opponent.starterDeckId) return null;

  const starterDeck = getStarterDeck(opponent.starterDeckId);
  if (!starterDeck) return null;

  // Same deal-parity rule buildNPCPlayerData (NPC.js) uses for every
  // other NPC seat: the hand comes OUT of the grimoire, never dealt
  // on top of a full, undepleted copy of it — "hand + grimoire = full
  // deck" (H15).
  const { hand, grimoire } = drawAndDepleteCards(starterDeck.grimoire || {}, [], 5);

  // THE BRAIN'S BRIEF (2026-09-06). The co-op villain plays at its own
  // authored skill, and its ids ride to the match so BOTH partners can
  // stamp their own story ledger at exit (recordStoryWin is per-uid —
  // rules let nobody write anyone else's profile). Rows spell the
  // difficulty both ways ("champion" / "Champion"); NPC.js wants the
  // capitalised form.
  const rawDiff = String(opponent.difficulty || "Champion");
  return {
    name: opponent.name,
    difficulty: rawDiff.charAt(0).toUpperCase() + rawDiff.slice(1).toLowerCase(),
    storyOpponentId: opponent.id || null,
    storyDungeonId:  (dungeon && dungeon.id) || opponent.dungeonId || null,
    grimoire,
    luxmoire: starterDeck.luxmoire || {},
    prophecy: starterDeck.prophecy || null,
    hand,
    curios:   starterDeck.curios   || [null, null],
    esclings: starterDeck.esclings || null,
  };
}